/**
 * Collapsible Navigation Group Component
 * Renders a group header with expand/collapse functionality and nested nav items
 * FEA-146: Added action support for opening session start modal
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import { NavGroup as NavGroupType, NavItem } from './navigationConfig';

interface NavGroupProps {
  group: NavGroupType;
  isExpanded: boolean;
  isSidebarExpanded: boolean;
  onToggle: () => void;
  activeGroupId: string | null;
}

export const NavGroup: React.FC<NavGroupProps> = ({
  group,
  isExpanded,
  isSidebarExpanded,
  onToggle,
  activeGroupId,
}) => {
  const isActiveGroup = group.id === activeGroupId;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Space key toggles expand/collapse (Enter already works via button default)
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onToggle();
    }
    // Arrow keys for navigation within groups
    if (e.key === 'ArrowDown' && isExpanded) {
      e.preventDefault();
      const firstLink = document.querySelector(`#nav-group-${group.id} a`) as HTMLElement;
      firstLink?.focus();
    }
  };

  const isDisabled = group.disabled === true;

  return (
    <li className={`nav-group ${isDisabled ? 'nav-group-disabled' : ''}`} role="none">
      <button
        className={`nav-group-header ${isActiveGroup ? 'active-group' : ''} ${isExpanded ? 'expanded' : ''} ${isDisabled ? 'disabled' : ''}`}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={isExpanded}
        aria-controls={`nav-group-${group.id}`}
        aria-haspopup="menu"
        title={isDisabled ? `${group.label} (Coming Soon)` : group.label}
        role="menuitem"
      >
        <span className="nav-icon" aria-hidden="true">
          {group.icon}
        </span>
        {isSidebarExpanded && (
          <>
            {group.phase !== undefined && (
              <span className="nav-phase-badge" aria-label={`Phase ${group.phase}`}>
                {group.phase}
              </span>
            )}
            <span className="nav-group-label">{group.label}</span>
            <span className={`nav-group-chevron ${isExpanded ? 'expanded' : ''}`} aria-hidden="true">
              &#9660;
            </span>
          </>
        )}
      </button>

      {isSidebarExpanded && (
        <ul
          id={`nav-group-${group.id}`}
          className={`nav-group-items ${isExpanded ? 'expanded' : 'collapsed'}`}
          role="menu"
          aria-label={`${group.label} navigation items`}
        >
          {group.items.map((item) => (
            <NavGroupItem key={item.path} item={item} />
          ))}
        </ul>
      )}
    </li>
  );
};

interface NavGroupItemProps {
  item: NavItem;
}

const NavGroupItem: React.FC<NavGroupItemProps> = ({ item }) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // ArrowUp moves focus to previous item or back to group header
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (e.currentTarget.parentElement?.previousElementSibling?.querySelector('a, button') ||
        e.currentTarget.closest('.nav-group')?.querySelector('.nav-group-header')) as HTMLElement;
      prev?.focus();
    }
    // ArrowDown moves to next item
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = e.currentTarget.parentElement?.nextElementSibling?.querySelector('a, button') as HTMLElement;
      next?.focus();
    }
  };

  return (
    <li role="none">
      <NavLink
        to={item.path}
        className={({ isActive }) => `nav-link nav-group-item ${isActive ? 'active' : ''}`}
        data-testid={item.testId}
        aria-label={`${item.label} page`}
        title={item.label}
        role="menuitem"
        onKeyDown={handleKeyDown}
      >
        <span className="nav-icon" aria-hidden="true">
          {item.icon}
        </span>
        <span>{item.label}</span>
        {item.badge !== undefined && (
          <span className="nav-badge" data-testid={`${item.testId}-badge`} aria-label={`${item.badge} items need attention`}>
            {item.badge}
          </span>
        )}
      </NavLink>
    </li>
  );
};
